import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { ChatPageContext } from './chatbot-page-context.service';
import { ChatbotGuestService } from './chatbot-guest.service';
import { AssistantAgentCatalogItem } from '../chatbot-ia/chatbot-slash-commands';

export interface ChatMessagePayload {
  role: 'user' | 'assistant';
  text: string;
  attachments?: ChatAttachmentRef[];
  actions?: ChatAction[];
  mentions?: { type: 'agent'; id: string; name?: string }[];
}

export interface ChatAttachmentRef {
  objectKey: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

export type ChatFileKind =
  | 'nota_fiscal'
  | 'chassi'
  | 'quote_pdf'
  | 'attachment'
  | 'generated_image';

export interface ChatFile {
  id: string;
  name: string;
  kind: ChatFileKind;
  mimeType: string;
  url: string;
  label?: string;
  fileSize?: number | null;
}

export interface ChatAction {
  label: string;
  url: string;
  /** `external` abre nova aba (Vindi, CDN, etc.); omitido = rota interna SPA. */
  target?: 'internal' | 'external';
}

export interface ChatConfirmationDetail {
  label: string;
  value: string;
}

export interface ChatConfirmation {
  id: string;
  toolName: string;
  label: string;
  title: string;
  summary: string;
  details: ChatConfirmationDetail[];
  confirmMessage: string;
  consumed?: boolean;
  refused?: boolean;
  agentId?: string;
  agentName?: string;
  requestedBy?: string;
}

export type ChatReportStatus = 'processing' | 'ready' | 'error';

export interface ChatReportFilter {
  label: string;
  value: string;
}

export interface ChatReportJob {
  id: string;
  name: string;
  description?: string;
  status: ChatReportStatus;
  countItems: number | null;
  fileXLSX: string | null;
  fileCSV: string | null;
  listingUrl?: string | null;
  appliedFilters?: ChatReportFilter[];
}

export interface ChatSource {
  toolName: string;
  title: string;
  agentId?: string;
  agentName?: string;
  url?: string | null;
}

export interface ChatFollowUp {
  label: string;
  prompt: string;
  mentionedAgentIds?: string[];
}

export interface ChatMemoryItem {
  id: string;
  content: string;
  category: string;
  source?: string;
  updatedAt?: string;
  createdAt?: string;
}

export interface AssistantChatResponse {
  reply: string;
  authenticated: boolean;
  isAdmin?: boolean;
  hierarchy?: string | null;
  conversationTitle?: string;
  conversationId?: string | null;
  actions?: ChatAction[];
  confirmations?: ChatConfirmation[];
  reports?: ChatReportJob[];
  files?: ChatFile[];
  sources?: ChatSource[];
  followUps?: ChatFollowUp[];
}

export interface AssistantQuickQuestionsResponse {
  headline: string;
  message: string;
  subtitle: string;
  quickQuestions: string[];
  generated?: boolean;
}

export interface AssistantTranscribeResponse {
  text: string;
}

export type ChatActivityPhase =
  | 'thinking'
  | 'searching'
  | 'tool'
  | 'tool_done'
  | 'confirm'
  | 'writing'
  | 'memory'
  | 'agent'
  | 'agent_done'
  | 'depth'
  | 'reflect'
  | 'title';

export type ChatReasoningDepth = 'low' | 'medium' | 'high' | 'max';

export type ChatActorKind = 'internal' | 'agent';

export interface ChatActivityStatus {
  phase: ChatActivityPhase;
  label: string;
  icon?: string;
  toolName?: string;
  detail?: string;
  /** URL completa de browse_web / browse_wosi_site (href do rastro — detail é só rótulo curto). */
  targetUrl?: string;
  agentId?: string;
  agentName?: string;
  actorId?: string;
  actorKind?: ChatActorKind;
  parallel?: boolean;
  depth?: ChatReasoningDepth;
  model?: string;
  conversationTitle?: string;
  /** Titulador devolveu KEEP — some do rastro. */
  titleIdle?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ChatbotIaService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private guestService = inject(ChatbotGuestService);

  private get headers(): HttpHeaders {
    const token = this.authService.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const guestId = this.guestService.getGuestId();
    if (guestId) {
      headers['X-Wosi-Chat-Guest'] = guestId;
    }
    return new HttpHeaders(headers);
  }

  private buildBody(
    messages: ChatMessagePayload[],
    pageContext?: ChatPageContext | null,
    conversationTitle?: string | null,
    lastListingUrl?: string | null,
    mentionedAgentIds?: string[] | null,
    responseStyle?: string | null,
    conversationId?: string | null,
    model?: string | null
  ): {
    messages: ChatMessagePayload[];
    pageContext?: ChatPageContext;
    conversationTitle?: string;
    lastListingUrl?: string;
    mentionedAgentIds?: string[];
    responseStyle?: string;
    conversationId?: string;
    model?: string;
  } {
    const body: {
      messages: ChatMessagePayload[];
      pageContext?: ChatPageContext;
      conversationTitle?: string;
      lastListingUrl?: string;
      mentionedAgentIds?: string[];
      responseStyle?: string;
      conversationId?: string;
      model?: string;
    } = { messages };

    if (pageContext) {
      body.pageContext = pageContext;
    }

    if (conversationTitle?.trim()) {
      body.conversationTitle = conversationTitle.trim();
    }

    if (lastListingUrl?.trim()) {
      body.lastListingUrl = lastListingUrl.trim();
    }

    if (mentionedAgentIds?.length) {
      body.mentionedAgentIds = mentionedAgentIds;
    }

    if (responseStyle?.trim()) {
      body.responseStyle = responseStyle.trim();
    }

    if (conversationId?.trim()) {
      body.conversationId = conversationId.trim();
    }

    if (model?.trim()) {
      body.model = model.trim();
    }

    return body;
  }

  listActiveAgents(): Observable<AssistantAgentCatalogItem[]> {
    return this.http
      .get<{ data?: AssistantAgentCatalogItem[] }>(
        `${environment.apiUrl}/assistant/agents`,
        { headers: this.headers }
      )
      .pipe(map((response) => response.data ?? []));
  }

  requestAttachmentPresigned(file: File): Observable<{
    url: string;
    objectKey: string;
    fileName: string;
    fileType: string;
    fileSize: number;
  }> {
    return this.http.post<{
      url: string;
      objectKey: string;
      fileName: string;
      fileType: string;
      fileSize: number;
    }>(
      `${environment.apiUrl}/assistant/attachments/presigned-url`,
      {
        fileName: file.name,
        fileType: inferChatAttachmentMime(file),
        fileSize: file.size,
      },
      { headers: this.headers }
    );
  }

  get attachmentVerifyEndpoint(): string {
    return `${environment.apiUrl}/assistant/attachments/verify`;
  }

  getQuickQuestions(
    pageContext?: ChatPageContext | null
  ): Observable<AssistantQuickQuestionsResponse> {
    const body: { pageContext?: ChatPageContext } = {};
    if (pageContext) {
      body.pageContext = pageContext;
    }

    return this.http.post<AssistantQuickQuestionsResponse>(
      `${environment.apiUrl}/assistant/quick-questions`,
      body,
      { headers: this.headers }
    );
  }

  transcribe(
    audioBase64: string,
    mimeType: string
  ): Observable<AssistantTranscribeResponse> {
    return this.http
      .post<AssistantTranscribeResponse>(
        `${environment.apiUrl}/assistant/transcribe`,
        { audioBase64, mimeType, language: 'pt-BR' },
        { headers: this.headers }
      )
      .pipe(
        timeout(20_000),
        map((response) => ({ text: String(response?.text ?? '').trim() }))
      );
  }

  ask(
    messages: ChatMessagePayload[],
    pageContext?: ChatPageContext | null,
    conversationTitle?: string | null,
    lastListingUrl?: string | null,
    mentionedAgentIds?: string[] | null,
    responseStyle?: string | null,
    conversationId?: string | null,
    model?: string | null
  ): Observable<AssistantChatResponse> {
    return this.http.post<AssistantChatResponse>(
      `${environment.apiUrl}/assistant/chat`,
      this.buildBody(
        messages,
        pageContext,
        conversationTitle,
        lastListingUrl,
        mentionedAgentIds,
        responseStyle,
        conversationId,
        model
      ),
      { headers: this.headers }
    );
  }

  async askStream(
    messages: ChatMessagePayload[],
    pageContext?: ChatPageContext | null,
    conversationTitle?: string | null,
    onStatus?: (status: ChatActivityStatus) => void,
    lastListingUrl?: string | null,
    mentionedAgentIds?: string[] | null,
    responseStyle?: string | null,
    signal?: AbortSignal | null,
    conversationId?: string | null,
    onDelta?: (text: string) => void,
    model?: string | null,
    onFollowUps?: (items: ChatFollowUp[]) => void
  ): Promise<AssistantChatResponse> {
    const token = this.authService.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const guestId = this.guestService.getGuestId();
    if (guestId) {
      headers['X-Wosi-Chat-Guest'] = guestId;
    }

    const response = await fetch(`${environment.apiUrl}/assistant/chat`, {
      method: 'POST',
      headers,
      credentials: 'include',
      signal: signal ?? undefined,
      body: JSON.stringify(
        this.buildBody(
          messages,
          pageContext,
          conversationTitle,
          lastListingUrl,
          mentionedAgentIds,
          responseStyle,
          conversationId,
          model
        )
      ),
    });

    if (!response.ok) {
      throw new Error(`assistant chat failed (${response.status})`);
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('text/event-stream')) {
      return (await response.json()) as AssistantChatResponse;
    }

    if (!response.body) {
      throw new Error('assistant stream vazio');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: AssistantChatResponse | null = null;
    let errorMessage: string | null = null;

    const applyBlock = (parsed: { event: string; data: unknown }): void => {
      if (parsed.event === 'status' && parsed.data) {
        try {
          onStatus?.(parsed.data as ChatActivityStatus);
        } catch {
          /* um status quebrado não pode dropar Acionando / browse */
        }
      }

      if (parsed.event === 'delta' && parsed.data) {
        const text = String((parsed.data as { text?: string }).text ?? '');
        if (text) onDelta?.(text);
      }

      if (parsed.event === 'result' && parsed.data) {
        result = parsed.data as AssistantChatResponse;
      }

      if (parsed.event === 'follow_ups' && parsed.data) {
        const items = (parsed.data as { followUps?: ChatFollowUp[] }).followUps;
        if (items?.length) onFollowUps?.(items);
      }

      if (parsed.event === 'error') {
        errorMessage =
          (parsed.data as { message?: string } | null)?.message ??
          'Erro ao processar mensagem do assistente.';
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      for (const chunk of chunks) {
        const parsed = parseSseBlock(chunk);
        if (parsed) applyBlock(parsed);
      }

      if (errorMessage) break;
      if (result) {
        void drainAssistantStream(reader, decoder, buffer, applyBlock);
        break;
      }
    }

    if (errorMessage) {
      throw new Error(errorMessage);
    }

    if (!result) {
      throw new Error('assistant stream sem resultado');
    }

    return result;
  }

  getReport(reportId: string): Observable<ChatReportJob> {
    return this.http
      .get<{
        data: {
          id: string;
          name: string;
          description?: string;
          status: ChatReportStatus;
          countItems: number | null;
          fileXLSX: string | null;
          fileCSV: string | null;
        };
      }>(`${environment.apiUrl}/reports/${reportId}`, { headers: this.headers })
      .pipe(
        map((response) => ({
          id: response.data.id,
          name: response.data.name,
          description: response.data.description,
          status: response.data.status,
          countItems: response.data.countItems,
          fileXLSX: response.data.fileXLSX,
          fileCSV: response.data.fileCSV,
        }))
      );
  }

  listConversations(): Observable<{ conversations: ChatConversationApiRecord[] }> {
    return this.http.get<{ conversations: ChatConversationApiRecord[] }>(
      `${environment.apiUrl}/assistant/conversations`,
      { headers: this.headers }
    );
  }

  getConversation(id: string): Observable<{ conversation: ChatConversationApiRecord }> {
    return this.http.get<{ conversation: ChatConversationApiRecord }>(
      `${environment.apiUrl}/assistant/conversations/${id}`,
      { headers: this.headers }
    );
  }

  upsertConversation(
    record: ChatConversationApiRecord
  ): Observable<{ conversation: ChatConversationApiRecord }> {
    return this.http.put<{ conversation: ChatConversationApiRecord }>(
      `${environment.apiUrl}/assistant/conversations/${record.id}`,
      record,
      { headers: this.headers }
    );
  }

  createConversation(
    record: ChatConversationApiRecord
  ): Observable<{ conversation: ChatConversationApiRecord }> {
    return this.http.post<{ conversation: ChatConversationApiRecord }>(
      `${environment.apiUrl}/assistant/conversations`,
      record,
      { headers: this.headers }
    );
  }

  deleteRemoteConversation(id: string): Observable<unknown> {
    return this.http.delete(
      `${environment.apiUrl}/assistant/conversations/${id}`,
      { headers: this.headers }
    );
  }

  importConversations(
    conversations: ChatConversationApiRecord[]
  ): Observable<{ conversations: ChatConversationApiRecord[] }> {
    return this.http.post<{ conversations: ChatConversationApiRecord[] }>(
      `${environment.apiUrl}/assistant/conversations/import`,
      { conversations },
      { headers: this.headers }
    );
  }

  claimConversations(): Observable<{ count: number }> {
    return this.http.post<{ count: number }>(
      `${environment.apiUrl}/assistant/conversations/claim`,
      {},
      { headers: this.headers }
    );
  }

  branchConversation(
    id: string,
    body: { messageId?: string; title?: string }
  ): Observable<{ conversation: ChatConversationApiRecord }> {
    return this.http.post<{ conversation: ChatConversationApiRecord }>(
      `${environment.apiUrl}/assistant/conversations/${id}/branch`,
      body,
      { headers: this.headers }
    );
  }

  exportConversation(id: string, format: 'md' | 'json'): Observable<Blob | { conversation: unknown }> {
    if (format === 'json') {
      return this.http.get<{ conversation: unknown }>(
        `${environment.apiUrl}/assistant/conversations/${id}/export`,
        { headers: this.headers, params: { format: 'json' } }
      );
    }
    return this.http.get(
      `${environment.apiUrl}/assistant/conversations/${id}/export`,
      { headers: this.headers, params: { format: 'md' }, responseType: 'blob' }
    );
  }

  shareConversation(id: string): Observable<{ token: string; expiresAt: string }> {
    return this.http.post<{ token: string; expiresAt: string }>(
      `${environment.apiUrl}/assistant/conversations/${id}/share`,
      {},
      { headers: this.headers }
    );
  }

  getSharedConversation(
    token: string
  ): Observable<{ conversation: ChatConversationApiRecord; readOnly?: boolean }> {
    return this.http.get<{ conversation: ChatConversationApiRecord; readOnly?: boolean }>(
      `${environment.apiUrl}/assistant/conversations/shared/${token}`,
      { headers: this.headers }
    );
  }

  listMemories(): Observable<{ memories: ChatMemoryItem[] }> {
    return this.http.get<{ memories: ChatMemoryItem[] }>(
      `${environment.apiUrl}/assistant/memories`,
      { headers: this.headers }
    );
  }

  updateMemory(
    id: string,
    patch: { content?: string; category?: string }
  ): Observable<{ memory: ChatMemoryItem }> {
    return this.http.patch<{ memory: ChatMemoryItem }>(
      `${environment.apiUrl}/assistant/memories/${id}`,
      patch,
      { headers: this.headers }
    );
  }

  deleteMemory(id: string): Observable<unknown> {
    return this.http.delete(`${environment.apiUrl}/assistant/memories/${id}`, {
      headers: this.headers,
    });
  }

  deleteAllMemories(): Observable<unknown> {
    return this.http.delete(`${environment.apiUrl}/assistant/memories`, {
      headers: this.headers,
    });
  }
}

export interface ChatConversationApiRecord {
  id: string;
  title: string;
  preview?: string | null;
  unread?: boolean;
  parentConversationId?: string | null;
  branchedFromMessageId?: string | null;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    text: string;
    displayText?: string;
    actions?: ChatAction[];
    confirmations?: ChatConfirmation[];
    process?: ChatActivityStatus[];
    reports?: ChatReportJob[];
    files?: ChatFile[];
    attachments?: ChatAttachmentRef[];
    mentions?: unknown[];
    sources?: ChatSource[];
    followUps?: ChatFollowUp[];
    revisions?: string[];
    revisionIndex?: number;
    interrupted?: boolean;
    createdAt: number;
  }>;
  createdAt: number;
  updatedAt: number;
}

function inferChatAttachmentMime(file: File): string {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/jpg') return 'image/jpeg';
  if (
    type === 'application/pdf' ||
    type === 'image/jpeg' ||
    type === 'image/png' ||
    type === 'image/webp'
  ) {
    return type;
  }

  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  return type || 'application/octet-stream';
}

async function drainAssistantStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  leftover: string,
  applyBlock: (parsed: { event: string; data: unknown }) => void
): Promise<void> {
  let buffer = leftover;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';
      for (const chunk of chunks) {
        const parsed = parseSseBlock(chunk);
        if (parsed) applyBlock(parsed);
      }
    }
    if (buffer.trim()) {
      const parsed = parseSseBlock(buffer);
      if (parsed) applyBlock(parsed);
    }
  } catch {
    /* follow-ups tardios são best-effort */
  }
}

function parseSseBlock(block: string): { event: string; data: unknown } | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const rawLine of block.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (!line || line.startsWith(':')) continue;

    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
      continue;
    }

    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (!dataLines.length) return null;

  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return { event, data: dataLines.join('\n') };
  }
}
